/** @jsxImportSource @emotion/react */

import { UserContext } from "context/UserContext";
import { useContext, useEffect, useState } from "react";
import { ReactTabulator } from "react-tabulator";
import tw from "twin.macro";

import "react-tabulator/css/bootstrap/tabulator_bootstrap.min.css";
import "react-tabulator/lib/styles.css";

import { CalciteLoader } from "@esri/calcite-components-react";
import { Button } from "components/Button";
import ExportButton from "components/ExportButton";
import FiltersDisplay from "components/FiltersDisplay";
import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Criteria } from "services/APIBase.d";
import { getELTExportColumns, getELTRecordColumns } from "services/ELTRecord.d";
import RecordService from "services/RecordService";
import { updateFooterText } from "utils/helpers";

const client = new RecordService("elt");
const ELTRecords: React.FC = () => {
  const { user } = useContext(UserContext);
  const [pageSize, setPageSize] = useState<number>(25);
  const [criterias, setCriterias] = useState<Criteria[]>([]);
  const [isTableReady, setIsTableReady] = useState(false);
  const [filters, setFilters] = useState<
    Array<{ field: string; value: string }>
  >([]);

  const hasEditPermission = user?.functions
    ? user.functions.includes("foresite:ELT:write")
    : false;

  const [eltCols] = useState(getELTRecordColumns(hasEditPermission));

  useEffect(() => {
    // Try to restore filters from localStorage
    const savedFilters = localStorage.getItem("ELTRecords-criteria");
    if (savedFilters) {
      console.log("Restoring filters from localStorage", savedFilters);
      try {
        const filters = JSON.parse(savedFilters);
        if (Array.isArray(filters) && filters.length > 0) {
          // Separate BETWEEN filters into criteria
          const betweenFilters = filters.filter((f) => f.type === "BETWEEN");
          if (betweenFilters.length > 0) {
            setCriterias(betweenFilters);
          }

          // Set remaining filters as initial filters
          const headerFilters = filters.filter((f) => f.type !== "BETWEEN");
          if (headerFilters.length > 0) {
            setFilters(headerFilters);
          }
        }
      } catch (e) {
        console.error("Error restoring filters:", e);
      }
    }
    setIsTableReady(true);
  }, []);

  const navigate = useNavigate();
  const clickRow = (e, row) => {
    const data = row.getData();
    const esn = data.ESN;

    if (esn) {
      navigate(`${esn}`);
    }
  };

  const tableRef = React.useRef<any>();
  // set tabulator options
  const options = {
    layout: "fitColumns",
    movableColumns: true,
    placeholder: "No ELT Records Found",
    // persist table state
    persistenceID: "ELTRecords1", // update ID param whenever table columns are updated
    persistenceMode: true, // store table state in local storage if possible, if not store in cookie
    persistence: {
      columns: ["width"], // persist column width; also persists column order
      page: true, // persist page number
    },
    // pagination
    pagination: true, // enable pagination
    paginationMode: "remote", // enable remote pagination
    paginationSize: pageSize, // default number of rows per page
    paginationSizeSelector: [10, 25, 50, 100], // page size options
    footerElement: "<span id='record-count'></span>",
    // filter
    filterMode: "remote", // send filter data to the server instead of processing locally
    initialHeaderFilter: filters,
    // sorting
    sortMode: "remote",
    initialSort: [{ column: "ESN", dir: "asc" }],
    // data request
    ajaxRequestFunc: function (url, config, params) {
      // convert default table params to API params
      let sortField = params.sort.length > 0 ? params.sort[0].field : "ESN";
      let sortDir = params.sort.length > 0 ? params.sort[0].dir : "asc";
      let firstRow = (params.page - 1) * params.size;
      let rangeStr = firstRow + "," + params.size;
      let savedCriterias = localStorage.getItem("ELTRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter((c) => c.type === "BETWEEN");
      }

      let reqBody = {
        range: rangeStr,
        sortBy: sortField,
        sortDirection: sortDir,
        criterias: [
          ...crit,
          ...params.filter.map((f) => ({
            field: f.field,
            type: f.type,
            value: f.value,
          })),
        ],
        full: true,
      };
      // log request body
      console.log("ELT Records table request body ", reqBody);
      return new Promise(function (resolve, reject) {
        // fetch records
        client
          .getRecords(reqBody, url)
          .then((res) => {
            // success -> return data
            resolve(res.data);
          })
          .catch((err) => {
            // error -> return error message
            console.error("ELT Records table error response", err);
            reject(err);
          });
      });
    },
    ajaxURL: "searchByCriteria", // API path
    ajaxResponse: (url, params, response) => {
      // log response
      console.log("ELT Records table response", response);
      // pagination footer text
      let currPageSize = tableRef.current.getPageSize();
      let currPage = tableRef.current.getPage();
      updateFooterText(
        currPage,
        currPageSize,
        response.message.size,
        response.message.totalRecordCount,
      );
      let savedCriterias = localStorage.getItem("ELTRecords-criteria");
      let crit: Array<{ field: string; type: string; value: string }> = [];
      if (savedCriterias) {
        crit = JSON.parse(savedCriterias).filter((c) => c.type === "BETWEEN");
      }

      // Store current filters if they exist
      let crits = [
        ...crit,
        ...params.filter.map((f) => ({
          field: f.field,
          type: f.type,
          value: f.value,
        })),
      ];

      if (crits && crits.length > 0) {
        console.log("Storing current criteria", crits);
        localStorage.setItem("ELTRecords-criteria", JSON.stringify(crits));
        setCriterias(crits);
      } else {
        console.log("Removing criteria");
        localStorage.removeItem("ELTRecords-criteria");
        setCriterias([]);
      }

      // return data to table
      let d = response.message.size > 0 ? response.message.list : [];
      let last =
        response.message.size > 0
          ? Math.ceil(response.message.totalRecordCount / currPageSize)
          : 0;
      return {
        data: d,
        last_page: last,
      };
    },
  };

  const handleClearFilters = () => {
    localStorage.removeItem("ELTRecords-criteria");
    setCriterias([]);
    setFilters([]);

    // Only attempt to clear filters if table is ready and ref exists
    if (isTableReady && tableRef.current) {
      tableRef.current.clearHeaderFilter();
      console.log("Cleared table filters");
    } else {
      console.log(
        "Table not ready or ref not found, filters cleared from state only",
      );
      // Force a table refresh to ensure filters are cleared
      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.setData();
        }
      }, 100);
    }
  };

  return (
    <>
      <div css={[tw`flex md:flex-nowrap flex-wrap justify-between gap-y-6`]}>
        <div css={[tw`flex flex-col space-y-4 w-full`]}>
          {criterias.some(
            (c) => c.type === "BETWEEN" && typeof c.value === "string",
          ) && (
            <div css={[tw`text-sm text-gray-600 font-medium`]}>
              {criterias
                .filter(
                  (c) => c.type === "BETWEEN" && typeof c.value === "string",
                )
                .map((c, index) => {
                  const [start, end] = (c.value as string).split(",");
                  return (
                    <span key={`${c.field}-${index}`}>
                      {`${c.field} Range: ${start} - ${end}`}
                      {index <
                      criterias.filter(
                        (c) =>
                          c.type === "BETWEEN" && typeof c.value === "string",
                      ).length -
                        1
                        ? " | "
                        : ""}
                    </span>
                  );
                })}
            </div>
          )}
          <div css={[tw`flex justify-between`]}>
            <div css={[tw`space-x-4 flex items-center`]}>
              <Button
                iconStart="search"
                onClick={() => tableRef.current?.setData()}
                variant="outline"
                kind="brand"
              >
                Filter
              </Button>
              <Button
                iconStart="x"
                onClick={handleClearFilters}
                variant="outline"
                kind="brand"
                disabled={criterias.length === 0}
              >
                Clear
              </Button>
              <FiltersDisplay criterias={criterias} cols={eltCols} />
            </div>
            <div css={[tw`space-x-4 flex items-center`]}>
              <ExportButton
                exportColumns={getELTExportColumns()}
                criterias={criterias}
                type="elt-record"
              />
            </div>
          </div>
        </div>
      </div>
      <div css={[tw` min-h-full border border-gray-300 rounded-md`]}>
        {isTableReady ? (
          <ReactTabulator
            id="ELTRecordsLoader"
            onRef={(r) => {
              tableRef.current = r.current;
            }}
            events={{
              // rowClick: clickRow,
              pageSizeChanged: function (pagesize) {
                setPageSize(pagesize);
              },
            }}
            options={options}
            columns={eltCols}
          />
        ) : (
          <div css={[tw`flex justify-center items-center h-32`]}>
            <CalciteLoader scale="s" label="Loading records..." />
          </div>
        )}
      </div>
      <Outlet />
    </>
  );
};

export default ELTRecords;
